import { createSelector, MemoizedSelector } from '@ngrx/store';
import {
    selectAllowNotificationPause,
    selectDisplayMeansOfTransport,
    selectEnableRemoteWork,
    selectEnableStandbyTravel,
    selectEnabledAfterHoursCallOut,
    selectTravelBillableOrderTypes,
} from '../../config/selectors/config.selector';
import { selectAllNotificationEntries } from '../../notification/selectors/notification.selector';
import { selectAddresses } from '../../address/selectors/address.selector';
import { ConfirmationTimesheetContext } from '../../utilities/confirmation-timesheet-context';
import { NotificationIdState } from '../../utilities/notification-id.type';
import { NotificationStateUtilities } from '../../utilities/notification-state-utlities';
import { TimesheetDerivationConfig, timesheetContextFor } from '../utils/derive-confirmation-entries';

/**
 * The config the confirmation-to-timesheet derivation branches on, gathered into one read.
 *
 * `travelBillableOrderTypes` stays the raw config value: a derivation covering several confirmations
 * has to answer billability per order type, and a factory selector per notification would rebuild
 * itself — and lose its memo — on every call.
 */
export const selectTimesheetDerivationConfig: MemoizedSelector<object, TimesheetDerivationConfig> = createSelector(
    selectEnableStandbyTravel,
    selectDisplayMeansOfTransport,
    selectTravelBillableOrderTypes,
    selectEnabledAfterHoursCallOut,
    selectEnableRemoteWork,
    (
        enableStandbyTravel,
        displayMeansOfTransport,
        travelBillableOrderTypes,
        enabledAfterHoursCallOut,
        enableRemoteWork,
    ) => ({
        enableStandbyTravel,
        displayMeansOfTransport,
        travelBillableOrderTypes,
        enabledAfterHoursCallOut,
        enableRemoteWork,
    }),
);

/**
 * Whether a confirmation's times reach the timesheet at all.
 *
 * The push this replaced only ran behind `AllowNotificationPause`, so on a market with it off a
 * confirmation's entries never appeared there. Deriving them without the same gate would put rows in
 * front of technicians who have never seen them — and, because an in-progress row blocks the
 * timesheet's own submit, would lock those technicians out of submitting at all.
 */
export const selectConfirmationEntriesReachTimesheet = selectAllowNotificationPause;

/**
 * Everything the timesheet needs to know about one confirmation's surroundings to derive its work
 * entries: the customer it is booked against, the address those entries carry, and the config
 * switches the derivation branches on.
 *
 * Gathered into one selector so a commit reads the store once rather than sampling four slices at
 * four different moments.
 *
 * Parameterized by notification, and so a factory — safe here because it is built once at a dispatch
 * site, never called per row inside a projector.
 *
 * @param notificationId the notification the confirmation belongs to
 * @returns the confirmation's timesheet context
 */
export const selectConfirmationTimesheetContext = (
    notificationId: NotificationIdState,
): MemoizedSelector<object, ConfirmationTimesheetContext> =>
    createSelector(
        selectAllNotificationEntries,
        selectAddresses,
        selectTimesheetDerivationConfig,
        (entries, addresses, config) =>
            timesheetContextFor(
                entries.find((entry) => NotificationStateUtilities.compare(entry.notificationId, notificationId)),
                addresses,
                config,
            ),
    );
